import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SmartRichText from "@/components/SmartRichText";
import MultiSelect from "@/components/ui/multi-select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import { SeriesEditorDialog } from "@/pages/app/AllMeetings";
import {
  Calendar, Trash2, MapPin, Sparkles, Repeat, Lock, Globe, Loader2,
  ClipboardList, FileText, Wand2, ListChecks, Plus, Download, Mail, X, History,
  Link2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const STATUS_AGENDA = ["pending", "discussed", "skipped"];
const STATUS_MEETING = ["scheduled", "in_progress", "done", "cancelled"];
const TASK_STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];

/**
 * Same content as the MeetingDetailDialog in ProjectMeetingsTab — but rendered
 * inline on the dedicated /app/meetings/:mid page.
 *
 * Iter 210:
 *   • Add agenda items inline (fixes "unable to add new agenda items")
 *   • Fallback to workspace members when projectMembers empty
 *     (fixes empty assignee dropdown for workspace-scoped meetings)
 *   • SmartRichText on discussion notes
 *   • Export MoM as branded PDF + email as attachment (with send-log)
 */
export default function MeetingDetailInline({ meetingId, projectMembers = [], myPerms = [], onChanged }) {
  const { activeWorkspaceId, user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || "";
  const canUpdate = myPerms.includes("meeting.update");
  const canCreateTask = myPerms.includes("task.create");
  const canUpdateTask = myPerms.includes("task.update");

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [notes, setNotes] = useState("");
  // notes is only kept here for the legacy save path if we need to revive it;
  // Discussion thread uses its own post-stream now (R293b).
  const [aiRunning, setAiRunning] = useState(false);
  const [wsMembers, setWsMembers] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/meetings/${meetingId}`);
      setData(data);
      setNotes(data.meeting.discussion_notes || "");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);

  // Fallback member source: workspace members (used when projectMembers is empty)
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`)
      .then((r) => setWsMembers(r.data || []))
      .catch(() => {});
  }, [activeWorkspaceId]);

  const memberList = useMemo(() => {
    // Prefer project members; fall back to workspace members. Normalise
    // shape → [{ user_id, name, email }]
    if (projectMembers && projectMembers.length > 0) {
      return projectMembers.map((m) => ({
        user_id: m.user_id, name: m.user?.name, email: m.user?.email,
      }));
    }
    return wsMembers.map((m) => ({ user_id: m.user_id || m.id, name: m.name, email: m.email }));
  }, [projectMembers, wsMembers]);

  // Feb 2026 R293c · For collaborator pickers on meeting action items,
  // always use the FULL active workspace roster (sorted alphabetically)
  // instead of the project-member subset, so a PM can loop in a client
  // success manager who isn't tied to the project yet.
  const workspaceRoster = useMemo(() => {
    return wsMembers
      .filter((m) => (m.status || "active") === "active")
      .map((m) => ({ user_id: m.user_id || m.id,
                      name: m.name, email: m.email }))
      .sort((a, b) => (a.name || a.email || "").toLowerCase()
                        .localeCompare((b.name || b.email || "").toLowerCase()));
  }, [wsMembers]);

  const patchMeeting = async (patch) => {
    try { await api.patch(`/meetings/${meetingId}`, patch); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const updateAgendaStatus = async (idx, status) => {
    const next = (data.meeting.agenda || []).map((a, i) => i === idx ? { ...a, status } : a);
    await patchMeeting({ agenda: next });
  };
  const removeAgenda = async (idx) => {
    const next = (data.meeting.agenda || []).filter((_, i) => i !== idx);
    await patchMeeting({ agenda: next });
  };
  const addAgenda = async ({ title, duration_minutes }) => {
    if (!title.trim()) return;
    const next = [...(data.meeting.agenda || []), { title: title.trim(), duration_minutes: duration_minutes || null, status: "pending" }];
    await patchMeeting({ agenda: next });
    toast.success("Agenda item added");
  };

  // Iter 253p · Turn any agenda item into an action item with a single tap.
  // The backend infers the assignee from `@handle` mentions or plain-name
  // matches inside the agenda description + discussion notes.
  const [busyAgendaTask, setBusyAgendaTask] = useState(null);
  const [seriesEditorOpen, setSeriesEditorOpen] = useState(false);
  const createTaskFromAgenda = async (agendaItem) => {
    if (!agendaItem?.id) {
      toast.error("Save the agenda item first, then convert it to a task.");
      return;
    }
    setBusyAgendaTask(agendaItem.id);
    try {
      const { data: res } = await api.post(
        `/meetings/${meetingId}/agenda/${agendaItem.id}/create-task`, {});
      const who = res?.task?.assignee_id
        ? (res.inferred_assignee ? "Assignee auto-detected." : "Assignee applied.")
        : "Assign it from the action-items list.";
      toast.success(`Task "${res.task.title}" created. ${who}`);
      // Refresh meeting (agenda item may have moved from pending→discussed).
      load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to create task")); }
    finally { setBusyAgendaTask(null); }
  };
  const [notesSaveState, setNotesSaveState] = useState("");
  // Feb 2026 R293d · Debounced autosave for Meeting Notes. Fires 1.5s
  // after the user stops typing so no edit is lost even if they close
  // the tab. Guard against the initial load state transition from "" →
  // actual loaded notes (which would otherwise trigger a save).
  const _notesLoaded = useRef(false);
  useEffect(() => {
    if (!_notesLoaded.current) {
      // Mark loaded once the initial data.meeting.discussion_notes
      // has been pushed into local state. notesSaveState stays "" so
      // we don't show "Unsaved" on first render.
      if (data?.meeting) _notesLoaded.current = true;
      return;
    }
    if (notesSaveState !== "dirty") return;
    const h = setTimeout(async () => {
      try {
        setNotesSaveState("saving");
        await patchMeeting({ discussion_notes: notes });
        setNotesSaveState("saved");
      } catch (e) {
        setNotesSaveState("dirty");
      }
    }, 1500);
    return () => clearTimeout(h);
  }, [notes, notesSaveState, data?.meeting]);
  const saveNotes = async () => {
    try {
      setNotesSaveState("saving");
      await patchMeeting({ discussion_notes: notes });
      setNotesSaveState("saved");
    } catch (e) { setNotesSaveState("dirty"); }
  };
  const summarize = async () => {
    setAiRunning(true);
    try {
      const { data: s } = await api.post(`/meetings/${meetingId}/summarize`, { extract_action_items: true });
      toast.success(`AI summary ready · ${s.action_items_created?.length || 0} action items extracted`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "AI summarise failed")); }
    finally { setAiRunning(false); }
  };
  const addAction = async (payload) => {
    await api.post(`/meetings/${meetingId}/action-items`, payload);
    await load();
  };
  // Iter Feb 2026 R35 · Remove an action item (soft-delete via new
  // DELETE /tasks/{tid}). Uses window.confirm — the modal-heavy shadcn
  // AlertDialog would be noise for a single-row remove.
  const removeAction = async (t) => {
    if (!window.confirm(`Remove action item "${t.title}"? This can't be undone from the UI.`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Action item removed");
      await load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to remove action item")); }
  };
  const exportPdf = async () => {
    try {
      const res = await api.get(`/meetings/${meetingId}/mom-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MoM_${(data.meeting.title || "meeting").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed")); }
  };

  if (busy || !data) return <div className="text-sm text-muted-foreground">Loading meeting…</div>;
  const m = data.meeting;
  const ais = data.action_items || [];
  // Feb 2026 R293a · Lock the meeting edits once marked done. The
  // backend enforces this too (423 Locked), but UI gating keeps the
  // intent obvious — buttons grey out and a banner explains the
  // discussion thread is still open.
  const isLocked = m.status === "done";

  return (
    <div className="space-y-5" data-testid="meeting-detail-inline">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {m.is_external ? <Globe className="w-5 h-5 text-emerald-500" /> : <Lock className="w-5 h-5 text-muted-foreground" />}
            <h1 className="text-2xl font-bold truncate" style={{ fontFamily: "var(--font-heading)" }}>{m.title}</h1>
            {m.series_id && <span className="chip text-[10px] inline-flex items-center"><Repeat className="w-3 h-3 mr-1" /> {m.series_index === 0 ? "series" : `#${m.series_index + 1}`}</span>}
          </div>
          <div className="text-xs text-muted-foreground">
            <Calendar className="w-3 h-3 inline mr-1" /> {new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min
            {m.location ? <> · <MapPin className="w-3 h-3 inline" /> {m.location}</> : null}
            {m.video_url ? <> · <a href={m.video_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">join</a></> : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={exportPdf} data-testid="meeting-mom-pdf">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download MoM
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setEmailOpen(true)} data-testid="meeting-mom-email">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Email MoM
          </Button>
          <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => setLogOpen(true)} data-testid="meeting-mom-log">
            <History className="w-3.5 h-3.5" />
          </Button>
          {canUpdate && !m.is_logged && m.scheduled_at && (
            <PushToGoogleButton
              meetingId={m.id}
              googleEventId={m.google_event_id}
              googleHtmlLink={m.google_html_link}
              onDone={load}
            />
          )}
          {canUpdate && m.series_id && (
            <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setSeriesEditorOpen(true)} data-testid="meeting-page-edit-series">
              <Repeat className="w-3.5 h-3.5 mr-1.5" /> Edit series
            </Button>
          )}
          {canUpdate && (
            <Select value={m.status || "scheduled"} onValueChange={(v) => patchMeeting({ status: v })}>
              <SelectTrigger className="h-8 text-xs w-40" data-testid="meeting-page-status"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_MEETING.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </header>

      {/* R153 · Feb 2026 — Attendee RSVP chips pulled from Google Calendar */}
      {(m.google_event_id || m.rsvp_summary) && (
        <RsvpPanel meeting={m} onRefresh={load} />
      )}

      {/* Feb 2026 R293a · Lock-on-done banner. Discussion thread stays
          open; everything else is read-only until an admin flips
          status back to Scheduled. */}
      {isLocked && (
        <div className="flex items-center gap-2 px-3 py-2 border border-amber-300 bg-amber-50 text-amber-900 text-xs"
             data-testid="meeting-locked-banner">
          <Lock className="w-4 h-4" />
          <span>
            <b>Meeting locked.</b> It's marked done, so edits to the
            title, agenda, attendees and action items are frozen. The
            Discussion thread below is still open — jot any after-notes
            there. Flip the status back to <i>Scheduled</i> if you need
            to reopen it.
          </span>
        </div>
      )}

      {/* Agenda */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Agenda · {(m.agenda || []).length}</div>
          {canUpdate && <AgendaAddInline onAdd={addAgenda} />}
        </div>
        <ul className="border border-border divide-y divide-border" data-testid="meeting-page-agenda">
          {(m.agenda || []).map((a, i) => (
            <li key={a.id || i} className="px-3 py-2 flex items-center gap-2 text-sm">
              <span className="flex-1 truncate">{a.title}</span>
              {a.duration_minutes && <span className="text-xs text-muted-foreground">{a.duration_minutes}m</span>}
              {canUpdate ? (
                <>
                  {canCreateTask && a.id && (
                    <button type="button"
                      onClick={() => createTaskFromAgenda(a)}
                      disabled={busyAgendaTask === a.id}
                      className="text-[10px] px-2 py-0.5 border border-border hover:bg-muted flex items-center gap-1 disabled:opacity-50"
                      data-testid={`meeting-agenda-to-task-${i}`}
                      title="Create an action item from this agenda item (assignee auto-inferred from notes)"
                    >
                      {busyAgendaTask === a.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <ListChecks className="w-3 h-3" />}
                      To task
                    </button>
                  )}
                  <Select value={a.status || "pending"} onValueChange={(v) => updateAgendaStatus(i, v)}>
                    <SelectTrigger className="h-6 text-[10px] w-28" data-testid={`meeting-agenda-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_AGENDA.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <button type="button" onClick={() => removeAgenda(i)} className="text-meta hover:text-destructive" data-testid={`meeting-agenda-del-${i}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : <span className="chip text-[10px]">{a.status}</span>}
            </li>
          ))}
          {(!m.agenda || m.agenda.length === 0) && <li className="px-3 py-2 text-xs text-muted-foreground">No agenda items yet. Use + Item to add one.</li>}
        </ul>
      </div>

      {/* R96 · External attendees editor — add / remove at runtime so meetings
          can invite clients or vendors after the fact, and their emails become
          available in the action-item assignee dropdown. */}
      <ExternalAttendeesEditor
        meeting={m}
        canUpdate={canUpdate}
        onSaved={(next) => patchMeeting({ external_attendees: next })}
      />

      {/* Feb 2026 R293g · Meeting Notes — the long free-text editor the
          team edits during the meeting. Autosaves every ~2s while the
          user types; a Save button is kept for an explicit confirm.
          The Comments post-stream below stays writable after lock. */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="label-eyebrow inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Meeting Notes</div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground"
                  data-testid="meeting-notes-save-status">
              {notesSaveState === "saving" ? "Saving…"
                 : notesSaveState === "saved" ? "Saved"
                 : notesSaveState === "dirty" ? "Unsaved"
                 : ""}
            </span>
            {canUpdate && (
              <Button size="sm" variant="outline"
                      className="rounded-none h-7 text-[10px]"
                      onClick={saveNotes}
                      disabled={notesSaveState === "saving" || notesSaveState === "saved" || notesSaveState === ""}
                      data-testid="meeting-notes-save-btn">
                Save
              </Button>
            )}
            {canUpdate && (
              <Button size="sm" className="rounded-none h-7 text-[10px]" onClick={summarize} disabled={aiRunning} data-testid="meeting-page-summarize">
                {aiRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                {aiRunning ? "Summarising…" : "AI summarise"}
              </Button>
            )}
          </div>
        </div>
        <SmartRichText
          value={notes}
          onChange={(v) => {
            setNotes(v);
            setNotesSaveState("dirty");
          }}
          minRows={6}
          maxHeight={480}
          mediaMode="url-only"
          workspaceId={activeWorkspaceId}
          placeholder="Capture decisions, debate, side notes. Autosaves every couple of seconds — the Save button is here for peace of mind."
          testid="meeting-page-notes"
        />
      </div>

      {m.ai_summary && (
        <div className="border-l-2 border-primary bg-primary/5 p-3 text-xs space-y-2" data-testid="meeting-page-ai-summary">
          <div className="label-eyebrow inline-flex items-center gap-1 text-primary"><Sparkles className="w-3 h-3" /> AI summary</div>
          <p className="text-sm">{m.ai_summary.summary}</p>
          {m.ai_summary.decisions?.length > 0 && (
            <>
              <div className="label-eyebrow mt-2">Decisions</div>
              <ul className="list-disc list-inside text-sm">{m.ai_summary.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </>
          )}
        </div>
      )}

      {/* Action items */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="label-eyebrow inline-flex items-center gap-1"><ListChecks className="w-3 h-3" /> Action items · {ais.length}</div>
          {canCreateTask && !isLocked && <ActionItemInline memberList={memberList} workspaceRoster={workspaceRoster} externalAttendees={m.external_attendees || []} onAdd={addAction} />}
        </div>
        <ul className="border border-border divide-y divide-border" data-testid="meeting-page-action-items">
          {ais.map((t) => (
            <li key={t.id} className="px-3 py-2 grid grid-cols-12 gap-2 items-start text-sm" data-testid={`meeting-page-ai-${t.id}`}>
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate">{t.title}</div>
                {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
                {t.assignee_hint && !t.assignee_id && (
                  <span className="chip text-[10px] inline-flex items-center gap-1 mt-1 bg-primary/10 text-primary border-primary/30" title="AI-suggested assignee — assign or dismiss" data-testid={`assignee-hint-${t.id}`}>
                    <Sparkles className="w-3 h-3" /> Suggested: {t.assignee_hint}
                  </span>
                )}
                {/* Feb 2026 R293a · Collaborator chips — attendees looped
                    in alongside the owner. Tiny avatars keep the row
                    compact; hover shows full name. */}
                {Array.isArray(t.collaborators) && t.collaborators.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 mt-1"
                       data-testid={`meeting-page-ai-collabs-${t.id}`}>
                    <span className="text-[10px] text-muted-foreground">With:</span>
                    {t.collaborators.map((c) => {
                      // Feb 2026 R293d · Prefer the FULL workspace roster
                      // (wsMembers) over the project-member subset so a
                      // collaborator who isn't on the project still
                      // resolves to a name instead of a raw UUID.
                      const mem = workspaceRoster.find((mm) => mm.user_id === c.user_id)
                               || memberList.find((mm) => mm.user_id === c.user_id);
                      const nm = (mem?.name || mem?.email || "Unknown user").slice(0, 24);
                      return (
                        <span key={c.user_id}
                              className="chip text-[9px] inline-flex items-center gap-1 bg-muted/60 text-muted-foreground border-border"
                              title={mem?.email || mem?.name || "Unknown user"}>
                          {nm}
                        </span>
                      );
                    })}
                  </div>
                )}
                {/* Feb 2026 R293a · Linked follow-up task strip. Shows
                    the linked task's title + status right under the
                    action item so PMs don't have to open the Task to
                    see the spin-off's state. */}
                {t.linked_task && (
                  <div className="mt-1 flex items-center gap-1 text-[10px]"
                       data-testid={`meeting-page-ai-linked-${t.id}`}>
                    <span className="text-muted-foreground">↳ Linked:</span>
                    <span className="font-medium text-foreground truncate">
                      {t.linked_task.title}
                    </span>
                    <span className="chip text-[9px] bg-muted/40 text-muted-foreground border-border">
                      {(t.linked_task.status || "open").replace("_", " ")}
                    </span>
                  </div>
                )}
              </div>
              <div className="col-span-2 text-xs">
                {t.external_assignee_email ? (
                  <span
                    className="chip text-[10px] inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 border-emerald-300"
                    title={t.external_assignee_email}
                    data-testid={`meeting-page-ai-ext-assignee-${t.id}`}
                  >
                    <Globe className="w-3 h-3" />
                    {t.external_assignee_name || t.external_assignee_email}
                  </span>
                ) : canUpdateTask ? (
                  <Select value={t.assignee_id || "_none"} onValueChange={(v) => api.patch(`/tasks/${t.id}`, { assignee_id: v === "_none" ? null : v }).then(load)}>
                    <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Unassigned</SelectItem>
                      {memberList.map((mem) => <SelectItem key={mem.user_id} value={mem.user_id}>{mem.name || mem.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : <span>{t.assignee_id || "—"}</span>}
              </div>
              <div className="col-span-2">
                {canUpdateTask ? (
                  <Input type="date" defaultValue={t.due_date?.slice(0,10) || ""} onBlur={(e) => api.patch(`/tasks/${t.id}`, { due_date: e.target.value || null }).then(load)} className="h-7 text-xs" />
                ) : <span className="text-xs">{t.due_date || "—"}</span>}
              </div>
              <div className="col-span-2">
                {canUpdateTask ? (
                  <Select value={t.status} onValueChange={(v) => api.patch(`/tasks/${t.id}`, { status: v }).then(load)}>
                    <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <span className="chip text-[10px]">{t.status}</span>}
              </div>
              <div className="col-span-1 text-right">
                <span className="chip text-[9px]">{t.priority}</span>
              </div>
              {/* Iter Feb 2026 R35 · Remove action item. Gated by task.update
                   perm (same gate as inline status/assignee edits) so juniors
                   who can't touch the task also can't drop it. */}
              <div className="col-span-1 flex justify-end gap-1">
                {/* Feb 2026 R293a · Spawn a linked follow-up task from
                    the action item. Idempotent on the backend — a
                    second click just surfaces the existing linked
                    task so no duplicate rows pile up. */}
                {canUpdateTask && !t.linked_task && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post(
                          `/meetings/${meetingId}/action-items/${t.id}/link-task`,
                          {});
                        toast.success("Linked task created");
                        load();
                      } catch (e) {
                        toast.error(apiErrorMessage(e, "Failed to link task"));
                      }
                    }}
                    className="p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-none border border-transparent hover:border-primary/30 transition-colors"
                    title="Create a linked follow-up task"
                    data-testid={`meeting-page-ai-link-task-${t.id}`}
                  >
                    <Link2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {canUpdateTask && (
                  <button
                    type="button"
                    onClick={() => removeAction(t)}
                    className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-none border border-transparent hover:border-destructive/30 transition-colors"
                    title="Remove action item"
                    data-testid={`meeting-page-ai-remove-${t.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
          {ais.length === 0 && <li className="px-3 py-2 text-xs text-muted-foreground">No action items yet.</li>}
        </ul>
      </div>

      {/* Feb 2026 R293d · Meeting Discussion — post-meeting comment
          stream. Separate from Meeting Notes (above) which is the
          long-form free-text editor. This section stays writable
          even after the meeting is marked done. */}
      <div>
        <div className="label-eyebrow inline-flex items-center gap-1 mb-1">
          <FileText className="w-3 h-3" /> Comments
        </div>
        <DiscussionThread
          meetingId={meetingId}
          currentUserId={currentUserId}
          canPost={canUpdate || (data?.meeting?.attendee_user_ids || []).includes(currentUserId)}
          onLegacyMigrate={async () => {
            await patchMeeting({ discussion_notes: "" });
            await load();
          }}
        />
      </div>

      {emailOpen && (
        <EmailMomDialog meetingId={meetingId} meeting={m} memberList={memberList} onClose={() => setEmailOpen(false)} />
      )}
      {logOpen && (
        <MomLogDialog meetingId={meetingId} onClose={() => setLogOpen(false)} />
      )}
      {seriesEditorOpen && (
        <SeriesEditorDialog
          meeting={m}
          onClose={() => setSeriesEditorOpen(false)}
          onSaved={() => { setSeriesEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function AgendaAddInline({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dur, setDur] = useState("");
  if (!open) {
    return <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(true)} data-testid="meeting-agenda-add-btn"><Plus className="w-3 h-3 mr-1" /> Item</Button>;
  }
  return (
    <form className="flex items-center gap-1"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!title.trim()) return;
            await onAdd({ title, duration_minutes: dur ? Number(dur) : null });
            setTitle(""); setDur(""); setOpen(false);
          }}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Agenda topic…" className="h-7 w-56 text-xs" autoFocus data-testid="meeting-agenda-add-title" />
      <Input value={dur} onChange={(e) => setDur(e.target.value)} placeholder="min" type="number" className="h-7 w-16 text-xs" data-testid="meeting-agenda-add-dur" />
      <Button type="submit" size="sm" className="rounded-none h-7 text-[10px]" data-testid="meeting-agenda-add-submit">Add</Button>
      <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(false)}>×</Button>
    </form>
  );
}

// R96 · Runtime external attendees editor for meetings. Renders the roster
// + inline add form + delete buttons. Any change PATCHes the whole array back
// to /meetings/{mid} which is idempotent (backend `_MeetingPatch` accepts
// `external_attendees`). Silent when caller lacks meeting.update.
function ExternalAttendeesEditor({ meeting, canUpdate, onSaved }) {
  const list = meeting.external_attendees || [];
  const [addOpen, setAddOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const addOne = async (e) => {
    e?.preventDefault?.();
    const n = name.trim(); const em = email.trim();
    if (!em) { toast.error("Email required"); return; }
    if (list.some((x) => (x.email || "").toLowerCase() === em.toLowerCase())) {
      toast.error("This attendee is already in the list"); return;
    }
    const next = [...list, { name: n || null, email: em }];
    await onSaved(next);
    setName(""); setEmail(""); setAddOpen(false);
  };
  const removeOne = async (email) => {
    const next = list.filter((x) => x.email !== email);
    await onSaved(next);
  };
  if (list.length === 0 && !canUpdate) return null;
  return (
    <div data-testid="meeting-externals-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow inline-flex items-center gap-1">
          <Globe className="w-3 h-3" /> External attendees · {list.length}
        </div>
        {canUpdate && !addOpen && (
          <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setAddOpen(true)} data-testid="meeting-externals-add-btn">
            <Plus className="w-3 h-3 mr-1" /> Attendee
          </Button>
        )}
      </div>
      {addOpen && canUpdate && (
        <form onSubmit={addOne} className="flex items-center gap-1 mb-2 flex-wrap">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" className="h-7 w-40 text-xs" data-testid="meeting-externals-add-name" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" className="h-7 w-56 text-xs" required autoFocus data-testid="meeting-externals-add-email" />
          <Button type="submit" size="sm" className="rounded-none h-7 text-[10px]" data-testid="meeting-externals-add-submit">Add</Button>
          <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => { setAddOpen(false); setName(""); setEmail(""); }}>×</Button>
        </form>
      )}
      {list.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">No external attendees. Add clients or vendors so they show up in the action-item assignee list.</div>
      ) : (
        <ul className="border border-border divide-y divide-border" data-testid="meeting-externals-list">
          {list.map((x) => (
            <li key={x.email} className="px-3 py-1.5 flex items-center gap-2 text-xs" data-testid={`meeting-externals-row-${x.email}`}>
              <Globe className="w-3 h-3 text-emerald-600 shrink-0" />
              <span className="font-medium truncate">{x.name || x.email}</span>
              {x.name && <span className="text-muted-foreground truncate">· {x.email}</span>}
              {canUpdate && (
                <button
                  type="button"
                  onClick={() => removeOne(x.email)}
                  className="ml-auto text-muted-foreground hover:text-destructive"
                  title="Remove attendee"
                  data-testid={`meeting-externals-remove-${x.email}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// end-ExternalAttendeesEditor

function ActionItemInline({ memberList, workspaceRoster = [], externalAttendees = [], onAdd }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");
  // Feb 2026 R293a · Collaborators looped into the action item alongside
  // the owner. Kept as an array (not Set) so the MultiSelect dropdown
  // can push updates straight into state. R293c · We now feed the
  // FULL workspace roster (sorted + searchable) instead of a chip
  // strip so admins can loop in anyone, not just the chips that fit.
  const [collabIds, setCollabIds] = useState([]);

  if (!open) {
    return <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(true)} data-testid="meeting-page-ai-add"><Plus className="w-3 h-3 mr-1" /> Action item</Button>;
  }
  // R96 · Assignee value formats:
  //   "_none"          → unassigned
  //   "user:<user_id>" → internal workspace member
  //   "ext:<email>"    → external attendee (name + email carried through)
  const isExt = assignee.startsWith("ext:");
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!title.trim()) return;
        const payload = { title, due_date: due || null, priority };
        if (isExt) {
          const email = assignee.slice(4);
          const ext = externalAttendees.find((x) => x.email === email);
          payload.assignee_id = null;
          payload.is_external = true;
          payload.external_assignee_email = email;
          payload.external_assignee_name = ext?.name || email;
        } else if (assignee.startsWith("user:")) {
          payload.assignee_id = assignee.slice(5);
        }
        // Feb 2026 R293a · Collaborators travel in the same payload.
        payload.collaborator_ids = collabIds;
        await onAdd(payload);
        setTitle(""); setAssignee(""); setDue(""); setPriority("medium");
        setCollabIds([]); setOpen(false);
      }}
      className="border border-border bg-muted/20 p-2 space-y-2"
      data-testid="meeting-page-ai-add-form"
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">New action item</div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px] gap-2">
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Action…" className="h-7 text-xs" autoFocus data-testid="meeting-page-ai-add-title" />
      {/* Feb 2026 R293d · Assignee picker is now a sorted + searchable
          dropdown driven by the full active workspace roster so any
          user can be assigned (not just project members). External
          attendees still appear as a separate group below. */}
      <div className="" data-testid="meeting-page-ai-add-assignee-wrap">
        <SearchableSelect
          value={assignee}
          onValueChange={(v) => setAssignee(v || "")}
          clearable
          width={240}
          placeholder="Assign to…"
          triggerClassName="h-7 w-full text-[10px]"
          testid="meeting-page-ai-add-assignee"
          options={[
            ...workspaceRoster.map((mem) => ({
              value: `user:${mem.user_id}`,
              label: mem.name || mem.email || "User",
            })),
            ...externalAttendees.map((ext) => ({
              value: `ext:${ext.email}`,
              label: `${ext.name || ext.email} · external`,
            })),
          ]}
        />
      </div>
      <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-7 text-xs" />
      </div>
      {/* Feb 2026 R293c · Collaborator picker — now a sorted,
          searchable multi-select driven by the full active workspace
          roster. Hidden assignee stays filtered out so an owner isn't
          both assignee and collaborator. */}
      {workspaceRoster.length > 0 && (
        <div className="w-full" data-testid="meeting-page-ai-add-collabs">
          <MultiSelect
            options={workspaceRoster
              .filter((mem) => `user:${mem.user_id}` !== assignee)
              .map((mem) => ({
                value: mem.user_id,
                label: mem.name || mem.email || "User",
                testid: `meeting-page-ai-add-collab-${mem.user_id}`,
              }))}
            value={collabIds}
            onChange={setCollabIds}
            placeholder="Loop in collaborators…"
            allLabel="Add collaborators"
            width="w-full"
            testid="meeting-page-ai-add-collabs-select"
          />
        </div>
      )}
      <div className="flex items-center justify-end gap-1">
        <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(false)}>Cancel</Button>
        <Button type="submit" size="sm" className="rounded-none h-7 text-[10px]">Add action item</Button>
      </div>
    </form>
  );
}

export function EmailMomDialog({ meetingId, meeting, memberList, onClose }) {
  const [subject, setSubject] = useState(`Minutes of Meeting — ${meeting?.title || ""}`);
  const [message, setMessage] = useState("Please find the meeting minutes attached.");
  const [freeText, setFreeText] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const togglePick = (email) => setPicked((prev) => {
    const n = new Set(prev);
    if (n.has(email)) n.delete(email); else n.add(email);
    return n;
  });

  const recipients = useMemo(() => {
    const free = freeText.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    return Array.from(new Set([...Array.from(picked), ...free]));
  }, [picked, freeText]);

  const send = async () => {
    if (recipients.length === 0) { toast.error("Pick at least one recipient"); return; }
    setBusy(true);
    try {
      await api.post(`/meetings/${meetingId}/email-mom`, {
        recipients, subject: subject.trim(), message: message || null,
      });
      toast.success(`MoM emailed to ${recipients.length} recipient(s)`);
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Email failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="meeting-mom-email-dialog">
        <DialogHeader><DialogTitle>Email meeting minutes as PDF</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Workspace members</Label>
            <div className="max-h-40 overflow-y-auto border border-border p-2 space-y-1">
              {memberList.map((m) => {
                if (!m.email) return null;
                return (
                  <label key={m.user_id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={picked.has(m.email)} onChange={() => togglePick(m.email)}
                           data-testid={`meeting-mom-email-member-${m.user_id}`} />
                    <span className="font-medium">{m.name || m.email}</span>
                    <span className="text-meta">{m.email}</span>
                  </label>
                );
              })}
              {memberList.length === 0 && <div className="text-xs text-meta">No members loaded.</div>}
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Additional emails (comma or space separated)</Label>
            <Input value={freeText} onChange={(e) => setFreeText(e.target.value)} placeholder="ceo@client.com, ops@partner.io" className="h-9 rounded-none text-xs" data-testid="meeting-mom-email-free" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-9 rounded-none text-xs" data-testid="meeting-mom-email-subject" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1.5 inline-block">Message body (plain text)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none text-xs" data-testid="meeting-mom-email-message" />
          </div>
          <div className="text-[11px] text-meta">
            {recipients.length} recipient(s). The sender + workspace owner / admins are automatically Cc&apos;d.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={send} disabled={busy || recipients.length === 0} data-testid="meeting-mom-email-send">
            {busy ? "Sending…" : `Send to ${recipients.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Feb 2026 R293b · Discussion thread for a meeting. Replaces the old
 * free-text `discussion_notes` box with an auditable post stream — each
 * post has an author + timestamp. The legacy field is backfilled by
 * the server as a single read-only "legacy notes" post so nothing is
 * lost on upgrade; a one-tap "Migrate" button on that legacy post
 * clears the free-text field once the new stream has caught up.
 */
function DiscussionThread({ meetingId, currentUserId, canPost = true,
                              onLegacyMigrate }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/meetings/${meetingId}/discussion`);
      setPosts(data?.rows || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load discussion"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);

  const submit = async () => {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    try {
      await api.post(`/meetings/${meetingId}/discussion`, { body });
      setDraft("");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to post"));
    } finally {
      setPosting(false);
    }
  };
  const remove = async (pid) => {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await api.delete(`/meetings/${meetingId}/discussion/${pid}`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to delete"));
    }
  };

  return (
    <div className="space-y-2" data-testid="meeting-discussion-thread">
      {canPost && (
        <div className="border border-border bg-background p-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment… the thread stays writable even after the meeting is marked done."
            rows={3}
            className="w-full text-sm bg-transparent focus:outline-none resize-none"
            data-testid="meeting-discussion-draft"
          />
          <div className="flex items-center justify-end mt-1">
            <Button
              size="sm" className="rounded-none h-7 text-[10px]"
              disabled={!draft.trim() || posting}
              onClick={submit}
              data-testid="meeting-discussion-post"
            >
              {posting ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      )}
      {loading && <div className="text-xs text-meta">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div className="text-xs text-meta italic">
          No discussion yet. Be the first to post.
        </div>
      )}
      {posts.map((p) => (
        <div key={p.id}
             className={`border border-border p-2 text-sm ${
               p.legacy ? "bg-amber-50/40" : "bg-background"
             }`}
             data-testid={`meeting-discussion-post-${p.id}`}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="text-[11px] font-semibold">
              {p.author_name || "Unknown"}
              {p.legacy && (
                <span className="chip text-[9px] ml-2 bg-amber-100 text-amber-900 border-amber-300">
                  Legacy
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="text-[10px] text-meta">
                {fmtRelative(p.created_at)}
              </div>
              {!p.legacy && p.author_user_id === currentUserId && (
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  className="p-0.5 text-muted-foreground hover:text-destructive"
                  title="Delete post"
                  data-testid={`meeting-discussion-delete-${p.id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
          <div className="whitespace-pre-wrap text-sm">{p.body}</div>
          {p.legacy && onLegacyMigrate && (
            <div className="mt-2 flex items-center justify-end">
              <Button
                size="sm" variant="outline"
                className="rounded-none h-7 text-[10px]"
                onClick={async () => {
                  if (!window.confirm(
                    "Archive the legacy notes field? Content will remain visible "
                    + "above as a one-off read-only post, and the old free-text "
                    + "box clears so new posts take over.")) return;
                  await onLegacyMigrate();
                  await load();
                }}
                data-testid="meeting-discussion-migrate"
              >
                Archive legacy notes
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


export function MomLogDialog({ meetingId, onClose }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  // Feb 2026 R293a · Acknowledgement roster — flat list of
  // (email, acknowledged_at) across every MoM email tied to the
  // meeting. Shown as a dedicated section so sender can scan who's
  // seen the minutes without scrolling individual sends.
  const [acks, setAcks] = useState([]);
  const [ackSummary, setAckSummary] = useState({ total: 0, acknowledged_count: 0 });
  useEffect(() => {
    api.get(`/meetings/${meetingId}/mom-log`)
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => {})
      .finally(() => setBusy(false));
    api.get(`/meetings/${meetingId}/mom-acknowledgements`)
      .then((r) => {
        setAcks(r.data?.rows || []);
        setAckSummary({
          total: r.data?.total || 0,
          acknowledged_count: r.data?.acknowledged_count || 0,
        });
      })
      .catch(() => setAcks([]));
  }, [meetingId]);
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="meeting-mom-log-dialog">
        <DialogHeader><DialogTitle>MoM send log · {rows.length}</DialogTitle></DialogHeader>
        {/* Feb 2026 R293a · Acknowledgement roster */}
        {acks.length > 0 && (
          <div className="border border-border bg-muted/20 p-2 text-xs"
               data-testid="meeting-mom-ack-roster">
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold uppercase tracking-widest text-[10px]">
                MoM acknowledgements
              </div>
              <div className="text-meta text-[10px]">
                {ackSummary.acknowledged_count} of {ackSummary.total} acknowledged
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {acks.map((a) => (
                <div key={a.email}
                     className="flex items-center gap-1 text-[11px]"
                     data-testid={`meeting-mom-ack-row-${a.email}`}>
                  {a.acknowledged_at ? (
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                  ) : (
                    <span className="w-3 h-3 inline-block border border-border rounded-full" />
                  )}
                  <span className="truncate">{a.email}</span>
                  {a.acknowledged_at && (
                    <span className="text-meta text-[10px] ml-auto">
                      {fmtRelative(a.acknowledged_at)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="max-h-96 overflow-y-auto space-y-2">
          {busy && <div className="text-xs text-meta">Loading…</div>}
          {!busy && rows.length === 0 && <div className="text-xs text-meta">Never emailed.</div>}
          {rows.map((r) => (
            <div key={r.id} className="border border-border p-2 text-xs" data-testid={`meeting-mom-log-row-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold">{r.sent_by_name}</div>
                <div className="text-meta text-[10px]">{fmtRelative(r.sent_at)}</div>
              </div>
              <div className="mt-1 text-meta">To: {(r.recipients || []).join(", ")}</div>
              {r.cc && r.cc.length > 0 && <div className="text-meta">Cc: {r.cc.join(", ")}</div>}
              <div className="mt-1">Subject: {r.subject}</div>
              {r.errors?.length > 0 && <div className="text-destructive mt-1">Errors: {r.errors.map((e) => e.to || e.error).join(", ")}</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// R151 · Feb 2026 — Push a single meeting to the host's connected
// Google Calendar. Uses `sendUpdates=all` server-side so external
// attendees receive proper .ics invites.
function PushToGoogleButton({ meetingId, googleEventId, googleHtmlLink, onDone }) {
  const [busy, setBusy] = useState(false);
  const push = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/push-to-google`);
      const label = data.action === "updated" ? "Updated" : "Sent";
      toast.success(`${label} to Google Calendar${data.attendees_notified ? ` · ${data.attendees_notified} invites` : ""}`);
      onDone && onDone();
    } catch (e) {
      const msg = apiErrorMessage(e, "Push failed");
      toast.error(msg.includes("Connect") || msg.includes("connect")
        ? "Connect your Google account first (Settings → Integrations)"
        : msg);
    } finally { setBusy(false); }
  };
  if (googleEventId && googleHtmlLink) {
    return (
      <div className="inline-flex border border-border h-8" data-testid="meeting-gcal-synced">
        <a
          href={googleHtmlLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs px-2.5 inline-flex items-center gap-1 hover:bg-muted"
          title="Open in Google Calendar"
        >
          <GoogleG /> Synced
        </a>
        <button
          type="button"
          onClick={push}
          disabled={busy}
          className="text-xs px-2.5 border-l border-border hover:bg-muted disabled:opacity-50"
          title="Push updates to Google Calendar"
          data-testid="meeting-gcal-resync"
        >
          {busy ? "…" : "resync"}
        </button>
      </div>
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      className="rounded-none h-8"
      onClick={push}
      disabled={busy}
      data-testid="meeting-push-to-gcal"
    >
      <GoogleG className="mr-1.5" />
      {busy ? "Sending…" : "Push to Google Calendar"}
    </Button>
  );
}

function GoogleG({ className = "" }) {
  return (
    <span
      className={`inline-block w-3.5 h-3.5 text-center leading-none font-bold ${className}`}
      style={{ fontFamily: "'Product Sans', Arial" }}
      aria-hidden
    >
      <span style={{ background: "linear-gradient(90deg,#4285F4,#EA4335,#FBBC05,#34A853)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>G</span>
    </span>
  );
}


// R153 · Feb 2026 — Attendee RSVP panel. Reads `meeting.attendee_rsvps`
// (populated at push time) and shows a compact summary + per-attendee
// row with status chip. A refresh button re-pulls from Google Calendar.
function RsvpPanel({ meeting, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const summary = meeting.rsvp_summary || { accepted: 0, declined: 0, tentative: 0, needs_action: 0 };
  const rsvps = meeting.attendee_rsvps || {};
  const entries = Object.entries(rsvps).sort(([, a], [, b]) => {
    const rank = { accepted: 0, tentative: 1, needs_action: 2, declined: 3 };
    return (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
  });
  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/meetings/${meeting.id}/refresh-rsvp`);
      toast.success("RSVPs refreshed");
      onRefresh && onRefresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Refresh failed")); }
    finally { setBusy(false); }
  };
  const total = (summary.accepted || 0) + (summary.declined || 0) + (summary.tentative || 0) + (summary.needs_action || 0);
  if (total === 0 && !meeting.google_event_id) return null;
  return (
    <div className="border border-border" data-testid="meeting-rsvp-panel">
      <div className="px-3 py-2 bg-muted/40 border-b border-border flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">RSVPs</span>
        {total === 0 ? (
          <span className="text-xs text-muted-foreground">No responses yet — attendees will show up here once they reply in Google Calendar.</span>
        ) : (
          <>
            {summary.accepted > 0 && <RsvpChip status="accepted" count={summary.accepted} />}
            {summary.tentative > 0 && <RsvpChip status="tentative" count={summary.tentative} />}
            {summary.declined > 0 && <RsvpChip status="declined" count={summary.declined} />}
            {summary.needs_action > 0 && <RsvpChip status="needs_action" count={summary.needs_action} />}
          </>
        )}
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="ml-auto text-[10px] uppercase tracking-widest font-semibold hover:text-primary disabled:opacity-50"
          data-testid="meeting-rsvp-refresh"
        >
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {entries.length > 0 && (
        <ul className="divide-y divide-border">
          {entries.map(([email, r]) => (
            <li key={email} className="px-3 py-1.5 flex items-center gap-2 text-xs" data-testid={`meeting-rsvp-row-${email}`}>
              <span className="flex-1 min-w-0 truncate">
                <span className="font-medium">{r.display_name || email}</span>
                {r.is_organizer && <span className="text-[9px] uppercase tracking-widest ml-1 text-primary">host</span>}
                {r.is_optional && <span className="text-[9px] uppercase tracking-widest ml-1 text-muted-foreground">optional</span>}
                {r.display_name && (
                  <span className="text-muted-foreground ml-2 truncate">{email}</span>
                )}
              </span>
              <RsvpChip status={r.status} />
            </li>
          ))}
        </ul>
      )}
      {meeting.rsvp_synced_at && (
        <div className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border" data-testid="meeting-rsvp-synced">
          Synced {new Date(meeting.rsvp_synced_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function RsvpChip({ status, count }) {
  const styles = {
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-300",
    declined: "bg-rose-50 text-rose-700 border-rose-300",
    tentative: "bg-amber-50 text-amber-700 border-amber-300",
    needs_action: "bg-muted text-muted-foreground border-border",
  };
  const labels = {
    accepted: "Accepted",
    declined: "Declined",
    tentative: "Maybe",
    needs_action: "No reply",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[10px] font-semibold uppercase tracking-widest ${styles[status] || styles.needs_action}`}
      data-testid={`rsvp-chip-${status}`}
    >
      {labels[status] || status}
      {count != null && count > 0 && <span className="font-mono">·{count}</span>}
    </span>
  );
}

